// Column definitions for the four Consistency DataTables.
// Mirrors the columns-file pattern used by Domain/domainCoverageColumns and
// Concept/definedTermsColumns. None of these columns close over drawer state,
// so they can be plain module-level constants.
import type { Column } from "../../../../components/DataTable";
import { toTitleCaseFromSnake } from "../../utils";
import type {
  CFinding,
  CInternalContradiction,
  CReferenceGap,
  CTerminologyIssue,
} from "./types";
import { referenceStatusTone, severityTone } from "./helpers";
import { SEVERITY_PILL_BASE_CLS } from "./constants";

function SeverityPill({
  severity,
}: {
  severity: CFinding["severity"] | string | null | undefined;
}) {
  const t = severityTone(severity);
  return (
    <span className={`${SEVERITY_PILL_BASE_CLS} ${t.bg} ${t.text}`}>
      {t.label}
    </span>
  );
}

export const contradictionColumns: Column<CInternalContradiction>[] = [
  {
    key: "id",
    header: "ID",
    align: "left",
    width: "70px",
    render: (row) => (
      <span className="text-[11.5px] font-semibold text-foreground">
        {row.contradiction_id}
      </span>
    ),
  },
  {
    key: "category",
    header: "Category",
    align: "left",
    width: "140px",
    render: (row) => (
      <span className="text-[11.5px] text-foreground break-words">
        {row.category}
      </span>
    ),
  },
  {
    key: "a",
    header: "Statement A",
    align: "left",
    minWidth: 220,
    ellipsis: 2,
    tooltip: (row) => `${row.statement_a} (${row.location_a})`,
    render: (row) => (
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[12px] text-foreground break-words">
          {row.statement_a}
        </span>
        <span className="text-[10.5px] text-muted-foreground break-words">
          {row.location_a}
        </span>
      </div>
    ),
  },
  {
    key: "b",
    header: "Statement B",
    align: "left",
    minWidth: 220,
    ellipsis: 2,
    tooltip: (row) => `${row.statement_b} (${row.location_b})`,
    render: (row) => (
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[12px] text-foreground break-words">
          {row.statement_b}
        </span>
        <span className="text-[10.5px] text-muted-foreground break-words">
          {row.location_b}
        </span>
      </div>
    ),
  },
  {
    key: "severity",
    header: "Severity",
    align: "center",
    width: "100px",
    render: (row) => <SeverityPill severity={row.severity} />,
  },
];

export const terminologyColumns: Column<CTerminologyIssue>[] = [
  {
    key: "term",
    header: "Canonical term",
    align: "left",
    minWidth: 150,
    render: (row) => (
      <span className="text-[12px] font-semibold text-foreground break-words">
        {row.term}
      </span>
    ),
  },
  {
    key: "variants",
    header: "Variants observed",
    align: "left",
    minWidth: 200,
    render: (row) => (
      <div className="flex flex-wrap items-center gap-1 min-w-0">
        {(row.variants ?? []).map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="inline-flex items-center rounded-md border border-border bg-background/60 px-1.5 py-0.5 text-[10.5px] text-foreground break-words"
          >
            {v}
          </span>
        ))}
      </div>
    ),
  },
  {
    key: "occurrences",
    header: "Occurrences",
    align: "center",
    width: "100px",
    render: (row) => (
      <span className="text-[12px] tabular-nums text-foreground">
        {row.occurrences}
      </span>
    ),
  },
  {
    key: "recommendation",
    header: "Recommendation",
    align: "left",
    minWidth: 220,
    ellipsis: 2,
    tooltip: (row) => row.recommendation,
    render: (row) => (
      <span className="text-[11.5px] text-muted-foreground break-words">
        {row.recommendation}
      </span>
    ),
  },
  {
    key: "severity",
    header: "Severity",
    align: "center",
    width: "100px",
    render: (row) => <SeverityPill severity={row.severity} />,
  },
];

export const referenceColumns: Column<CReferenceGap>[] = [
  {
    key: "referenced_item",
    header: "Referenced item",
    align: "left",
    minWidth: 200,
    render: (row) => (
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[12px] font-semibold text-foreground break-words">
          {row.referenced_item}
        </span>
        <span className="text-[10.5px] text-muted-foreground">
          {row.reference_type}
        </span>
      </div>
    ),
  },
  {
    key: "found_in",
    header: "Found in",
    align: "left",
    width: "180px",
    render: (row) => (
      <span className="text-[11.5px] text-muted-foreground break-words">
        {row.found_in}
      </span>
    ),
  },
  {
    key: "note",
    header: "Note",
    align: "left",
    minWidth: 220,
    ellipsis: 2,
    tooltip: (row) => row.note,
    render: (row) => (
      <span className="text-[11.5px] text-muted-foreground break-words">
        {row.note}
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    align: "center",
    width: "120px",
    render: (row) => {
      const t = referenceStatusTone(row.status);
      return (
        <span className={`${SEVERITY_PILL_BASE_CLS} ${t.bg} ${t.text}`}>
          {t.label}
        </span>
      );
    },
  },
];

export const findingColumns: Column<CFinding>[] = [
  {
    key: "finding_type",
    header: "Finding",
    align: "left",
    minWidth: 160,
    render: (row) => (
      <span className="text-[12px] font-semibold text-foreground break-words">
        {toTitleCaseFromSnake(row.finding_type)}
      </span>
    ),
  },
  {
    key: "affected_area",
    header: "Affected area",
    align: "left",
    minWidth: 180,
    render: (row) => (
      <span className="text-[12px] text-foreground break-words">
        {row.affected_area}
      </span>
    ),
  },
  {
    key: "description",
    header: "Description",
    align: "left",
    ellipsis: 2,
    tooltip: (row) => row.description,
    render: (row) => (
      <span className="text-[12px] text-muted-foreground break-words">
        {row.description}
      </span>
    ),
  },
  {
    key: "severity",
    header: "Severity",
    align: "center",
    width: "100px",
    render: (row) => <SeverityPill severity={row.severity} />,
  },
  {
    key: "source",
    header: "Source",
    align: "left",
    width: "140px",
    render: (row) => (
      <span className="text-[11px] text-muted-foreground break-words">
        {(row.source_location ?? []).join(", ")}
      </span>
    ),
  },
];
